import { useState, type JSX } from 'react';
import type { MonitorEvent } from '@netdeck/contracts';
import { formatMoment } from '../../shared/format/time';
import { currentLanguage, useTranslation } from '../../shared/i18n';
import { useErrorText } from '../../shared/i18n/useErrorText';
import { DataTable, type Column } from '../../shared/ui/DataTable';
import { EmptyState, ErrorState, Loading, Panel } from '../../shared/ui/Panel';
import { StatusDot, type Status } from '../../shared/ui/StatusDot';
import { useEvents, useSites } from '../nodes/api';

/**
 * Лента событий площадки (§11, фаза 3, задача 11).
 *
 * Показываются только смены состояния, а не каждая проверка: при тысяче
 * мониторов с интервалом 30 секунд поток проверок — это две тысячи записей
 * в минуту, в которых ничего не видно. Смена состояния — то, на что
 * действительно нужно реагировать.
 */
export function EventsPage(): JSX.Element {
  const { t } = useTranslation();
  const errorText = useErrorText();
  const language = currentLanguage();

  const sites = useSites();
  const [siteId, setSiteId] = useState<string | null>(null);

  const activeSiteId = siteId ?? sites.data?.[0]?.id ?? null;
  const events = useEvents(activeSiteId);

  const columns: Column<MonitorEvent>[] = [
    {
      key: 'status',
      header: '',
      width: '20px',
      render: (event) => (
        <StatusDot status={toStatus(event.toStatus)} label={transitionLabel(event)} />
      ),
    },
    {
      key: 'at',
      header: t('events.column.at'),
      width: '0.9fr',
      priority: 'summary',
      render: (event) => (
        <span className="font-mono text-muted">{formatMoment(event.at, language)}</span>
      ),
    },
    {
      key: 'node',
      header: t('events.column.node'),
      width: '1.2fr',
      priority: 'title',
      render: (event) => <span className="block truncate font-medium">{event.nodeName}</span>,
    },
    {
      key: 'monitor',
      header: t('events.column.monitor'),
      width: '0.7fr',
      render: (event) => <span className="font-mono text-muted">{event.monitorKind}</span>,
    },
    {
      key: 'transition',
      header: t('events.column.transition'),
      width: '1fr',
      priority: 'summary',
      render: (event) => (
        <span className={`font-mono ${event.toStatus === 'down' ? 'text-down-fg' : 'text-muted'}`}>
          {event.fromStatus} → {event.toStatus}
        </span>
      ),
    },
    {
      key: 'error',
      header: t('events.column.reason'),
      width: '1.2fr',
      render: (event) => (
        <span className="block truncate font-mono text-xs text-dim">
          {event.error ?? t('common.dash')}
        </span>
      ),
    },
  ];

  if (sites.isPending) {
    return <Loading text={t('sites.loading')} />;
  }

  if (activeSiteId === null) {
    return <EmptyState title={t('sites.emptyTitle')} hint={t('events.noSitesHint')} />;
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col p-4">
      <Panel
        title={t('events.title')}
        subtitle={t('events.subtitle', { count: events.data?.length ?? 0 })}
        actions={
          <select
            value={activeSiteId}
            onChange={(event) => setSiteId(event.target.value)}
            aria-label={t('sites.one')}
            className="h-6.5 rounded-sm border border-line-strong bg-surface px-1.5 text-sm text-fg"
          >
            {(sites.data ?? []).map((site) => (
              <option key={site.id} value={site.id}>
                {site.name}
              </option>
            ))}
          </select>
        }
      >
        {events.isPending ? (
          <Loading />
        ) : events.error ? (
          <ErrorState
            title={t('events.loadFailed')}
            hint={t('events.loadFailedHint', { reason: errorText(events.error) })}
          />
        ) : (
          <DataTable
            columns={columns}
            rows={events.data ?? []}
            rowKey={(event) => event.id}
            rowDanger={(event) => event.toStatus === 'down'}
            empty={<EmptyState title={t('events.emptyTitle')} hint={t('events.emptyHint')} />}
          />
        )}
      </Panel>
    </div>
  );
}

function toStatus(value: MonitorEvent['toStatus']): Status {
  return value === 'up' ? 'up' : value === 'down' ? 'down' : 'unknown';
}

function transitionLabel(event: MonitorEvent): string {
  return `${event.nodeName}: ${event.fromStatus} → ${event.toStatus}`;
}
